import React, { useState } from 'react';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';

const Roster: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const { data: assignments, loading, error } = useApi(
    () =>
      apiService.getRosterAssignments({
        start_date: format(monthStart, 'yyyy-MM-dd'),
        end_date: format(monthEnd, 'yyyy-MM-dd'),
      }),
    [currentMonth]
  );

  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getAssignmentsForDay = (day: Date) => {
    return assignments?.filter((assignment) =>
      isSameDay(new Date(assignment.start_time), day)
    ) || [];
  };

  const getDutyTypeColor = (dutyType: string) => {
    switch (dutyType.toLowerCase()) {
      case 'flight':
        return 'bg-sky-500/20 text-sky-400 border-sky-500/30';
      case 'standby':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'training':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'leave':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Roster</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage crew assignments and schedules
          </p>
        </div>
        <button
          onClick={() => alert('New assignment form coming soon')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          New Assignment
        </button>
      </div>

      {/* Calendar Controls */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="btn-secondary p-2"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <h2 className="text-xl font-semibold text-white">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>

          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="btn-secondary p-2"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Calendar */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="glass-card p-6 text-center text-red-400">{error.message}</div>
      ) : (
        <div className="glass-card p-6">
          {/* Week Days Header */}
          <div className="mb-4 grid grid-cols-7 gap-2">
            {weekDays.map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-400">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Empty cells for days before month starts */}
            {Array.from({ length: monthStart.getDay() }).map((_, i) => (
              <div key={`empty-${i}`} className="h-32 rounded-lg border border-gray-800/50"></div>
            ))}

            {/* Days */}
            {daysInMonth.map((day) => {
              const dayAssignments = getAssignmentsForDay(day);
              const isToday = isSameDay(day, new Date());

              return (
                <div
                  key={day.toISOString()}
                  className={`h-32 overflow-hidden rounded-lg border p-2 transition-colors ${
                    isToday
                      ? 'border-sky-500 bg-sky-500/5'
                      : 'border-gray-800 bg-gray-900/30 hover:bg-gray-800/50'
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span
                      className={`text-sm font-medium ${
                        isToday ? 'text-sky-400' : 'text-gray-400'
                      }`}
                    >
                      {format(day, 'd')}
                    </span>
                    {dayAssignments.length > 0 && (
                      <span className="rounded-full bg-sky-500/20 px-1.5 py-0.5 text-xs text-sky-400">
                        {dayAssignments.length}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    {dayAssignments.slice(0, 2).map((assignment) => (
                      <div
                        key={assignment.id}
                        className={`rounded border px-1.5 py-1 text-xs ${getDutyTypeColor(
                          assignment.duty_type
                        )}`}
                      >
                        {assignment.duty_type}
                      </div>
                    ))}
                    {dayAssignments.length > 2 && (
                      <div className="text-xs text-gray-400">
                        +{dayAssignments.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="glass-card p-4">
        <h3 className="mb-3 text-sm font-medium text-gray-400">Duty Types</h3>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-sky-500"></div>
            <span className="text-sm text-gray-300">Flight</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-yellow-500"></div>
            <span className="text-sm text-gray-300">Standby</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-purple-500"></div>
            <span className="text-sm text-gray-300">Training</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-green-500"></div>
            <span className="text-sm text-gray-300">Leave</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roster;
